// Shared math for the service-lane baseline → projections. Lives in lib so
// the Service Lane settings, Goals & Rewards settings, the advisor adoption
// dashboard, and the email previews all derive numbers from one place.
//
// Industry defaults are applied when a field is unset so a brand-new
// dealership still sees plausible suggestions before they've filled the form.
// The defaults track the doc: ~40% visible damage, $5K avg RO, 5% conversion.

import type { ServiceLaneBaseline } from "@/lib/api/types"

export const BASELINE_DEFAULTS = {
  visibleDamagePct: 40,
  avgRoValue: 5000,
  targetConversionRate: 5,
} as const

// Service lanes typically run 5 days/week (Mon–Fri). 4.33 weeks/month is the
// standard pro-rata for monthly conversion. These are pinned to keep the math
// stable across views — if a dealership runs 6-day or 24/7 service, we can
// expose a knob later.
export const DAYS_PER_WEEK = 5
export const WEEKS_PER_MONTH = 4.33

export interface BaselineProjection {
  // Inputs (after defaults applied)
  dailyLaneVolume: number
  visibleDamagePct: number
  avgRoValue: number
  targetConversionRate: number

  // Derived
  viableEstimatesPerDay: number
  viableEstimatesPerWeek: number
  viableEstimatesPerMonth: number

  // $ projections at the viable ceiling
  projectedWeeklyGrossProfit: number
  projectedMonthlyGrossProfit: number

  // True if the dealership has set their lane volume — without it nothing
  // projects (we can't fake the input).
  hasInputs: boolean
}

export function projectBaseline(baseline: ServiceLaneBaseline | undefined): BaselineProjection {
  const dailyLaneVolume = baseline?.dailyLaneVolume ?? 0
  const visibleDamagePct = baseline?.visibleDamagePct ?? BASELINE_DEFAULTS.visibleDamagePct
  const avgRoValue = baseline?.avgRoValue ?? BASELINE_DEFAULTS.avgRoValue
  const targetConversionRate =
    baseline?.targetConversionRate ?? BASELINE_DEFAULTS.targetConversionRate

  const viableEstimatesPerDay = dailyLaneVolume * (visibleDamagePct / 100)
  const viableEstimatesPerWeek = viableEstimatesPerDay * DAYS_PER_WEEK
  const viableEstimatesPerMonth = viableEstimatesPerWeek * WEEKS_PER_MONTH

  const projectedWeeklyGrossProfit =
    viableEstimatesPerWeek * (targetConversionRate / 100) * avgRoValue
  const projectedMonthlyGrossProfit =
    viableEstimatesPerMonth * (targetConversionRate / 100) * avgRoValue

  return {
    dailyLaneVolume,
    visibleDamagePct,
    avgRoValue,
    targetConversionRate,
    viableEstimatesPerDay,
    viableEstimatesPerWeek,
    viableEstimatesPerMonth,
    projectedWeeklyGrossProfit,
    projectedMonthlyGrossProfit,
    hasInputs: dailyLaneVolume > 0,
  }
}

// Project the $ value of any goal — what hitting `goalEstimates` per
// `cadence` is worth at the dealership's conversion × avg RO. Used by the
// Goals screen to show "if you hit 48 estimates/wk = $12K projected".
export function projectGoalValue(
  baseline: ServiceLaneBaseline | undefined,
  goalEstimates: number,
): number {
  const visibleDamagePct = baseline?.visibleDamagePct ?? BASELINE_DEFAULTS.visibleDamagePct
  const avgRoValue = baseline?.avgRoValue ?? BASELINE_DEFAULTS.avgRoValue
  const targetConversionRate =
    baseline?.targetConversionRate ?? BASELINE_DEFAULTS.targetConversionRate
  // visibleDamagePct isn't used in the $ projection itself — the conversion
  // rate is applied to *estimates written*, not to lane traffic — but we read
  // it here to keep the BASELINE_DEFAULTS audit surface single-pointed.
  void visibleDamagePct
  return goalEstimates * (targetConversionRate / 100) * avgRoValue
}

export function formatMoney(n: number): string {
  if (!Number.isFinite(n) || n <= 0) return "$0"
  return n >= 1000
    ? `$${(n / 1000).toFixed(n >= 10000 ? 0 : 1)}K`
    : `$${Math.round(n).toLocaleString("en-US")}`
}

export function formatMoneyExact(n: number): string {
  if (!Number.isFinite(n) || n <= 0) return "$0"
  return `$${Math.round(n).toLocaleString("en-US")}`
}

// Pay-model rate suggestion. The Service Lane calculator gives the manager
// three logical rate pairs (per submitted estimate + per paid estimate)
// anchored to a target % of the projected revenue per advisor per month.
//
// Anchor rationale:
//   • Per-paid-estimate rate is the meaningful one — that's where the
//     dealership actually captures revenue. We back-solve it as a slice
//     of the average gross profit per paid estimate at the configured
//     conversion rate × avg RO.
//   • Per-submitted rate is much smaller, intentionally — it's the
//     habit-fuel that gets the advisor to write *any* estimate at all.
//     We anchor it to ~5-10% of the per-paid rate so the math is
//     internally consistent (write 10, paid 1, get ~one full per-paid
//     rate's worth from the submitted side too).
//
// All three tiers round to clean $5 increments so the manager sees
// "$5 / $50" not "$4.73 / $46.20".

export interface RateSuggestion {
  perEstimate: number
  perPaid: number
  // Projected monthly compensation per advisor at the configured viable
  // estimate volume / conversion rate / advisor count.
  advisorMonthly: number
  // What fraction of the projected gross profit the dealership shares with
  // the advisor pool at this rate. Used for the "you share X% of upside"
  // line on each tier card.
  shareOfUpside: number
}

export interface RateSuggestionTriple {
  conservative: RateSuggestion
  standard: RateSuggestion
  generous: RateSuggestion
}

function roundDownTo5(n: number): number {
  return Math.max(0, Math.floor(n / 5) * 5)
}

function buildSuggestion(args: {
  perEstimate: number
  perPaid: number
  baseline: ServiceLaneBaseline | undefined
  activeAdvisors: number
}): RateSuggestion {
  const proj = projectBaseline(args.baseline)
  // Each advisor's slice of the viable monthly volume. The baseline
  // projects the full lane; pay scales with what each advisor actually
  // writes, not what the dealership theoretically could.
  const monthlyEstimatesPerAdvisor =
    proj.viableEstimatesPerMonth / Math.max(1, args.activeAdvisors)
  const conversionRate = proj.targetConversionRate / 100
  const monthlyPaidPerAdvisor = monthlyEstimatesPerAdvisor * conversionRate
  const advisorMonthly =
    monthlyEstimatesPerAdvisor * args.perEstimate +
    monthlyPaidPerAdvisor * args.perPaid
  const monthlyAdvisorPoolCost = advisorMonthly * Math.max(1, args.activeAdvisors)
  const shareOfUpside =
    proj.projectedMonthlyGrossProfit > 0
      ? monthlyAdvisorPoolCost / proj.projectedMonthlyGrossProfit
      : 0
  return {
    perEstimate: args.perEstimate,
    perPaid: args.perPaid,
    advisorMonthly: Math.round(advisorMonthly),
    shareOfUpside: Math.max(0, shareOfUpside),
  }
}

/**
 * Suggest three pay-rate pairs from the dealership's lane baseline.
 * Conservative ≈ 3% upside share, Standard ≈ 7%, Generous ≈ 12% — but
 * each is rounded to clean $5 increments to feel chosen rather than
 * calculated.
 */
export function suggestRateTriple(
  baseline: ServiceLaneBaseline | undefined,
  activeAdvisors: number,
): RateSuggestionTriple {
  const proj = projectBaseline(baseline)
  // Profit per paid estimate (the conversion already happened, so we just
  // divide GP by paid-estimate volume). When the baseline isn't filled in,
  // fall back to a $250 anchor (5% × $5K avg RO).
  const profitPerPaid =
    proj.viableEstimatesPerMonth > 0 && proj.projectedMonthlyGrossProfit > 0
      ? proj.projectedMonthlyGrossProfit /
        (proj.viableEstimatesPerMonth * (proj.targetConversionRate / 100))
      : 250

  // Tier multipliers — fraction of profit-per-paid the dealership routes
  // to the advisor. Anchored to typical body-shop referral spiffs:
  //   • Conservative ~1% (e.g. $50 on a $5K profit RO) — habit-fuel only
  //   • Standard ~2.5%   (e.g. $125 on a $5K profit RO) — common range
  //   • Generous ~5%     (e.g. $250 on a $5K profit RO) — competitive
  // Anything higher cuts into the dealership's margin too aggressively.
  const tiers = [
    { key: "conservative" as const, share: 0.01 },
    { key: "standard" as const, share: 0.025 },
    { key: "generous" as const, share: 0.05 },
  ]

  // Per-submitted is anchored to ~8-12% of per-paid. Writing the estimate
  // has value (converts customer intent into a tracked record), but a
  // paid estimate is ~10× more valuable to the dealership, so the ratio
  // stays small.
  const submittedRatio = [0.08, 0.1, 0.1]

  const built: Partial<RateSuggestionTriple> = {}
  tiers.forEach((tier, idx) => {
    const perPaidRaw = profitPerPaid * tier.share
    const perPaid = Math.max(5, roundDownTo5(perPaidRaw))
    const perEstimateRaw = perPaid * submittedRatio[idx]
    const perEstimate = Math.max(2, Math.round(perEstimateRaw))
    built[tier.key] = buildSuggestion({
      perEstimate,
      perPaid,
      baseline,
      activeAdvisors,
    })
  })

  return built as RateSuggestionTriple
}
